// CrisisSupport.tsx — a calm, immediate help page. Cloned from the reduced-version
// app's SafetyPage (999 + UK domestic-abuse helplines), restyled to this app.
// Opened from the "I am feeling at risk" choice and from the CARE reflection.
import React from 'react';
import { openExternal } from '@/services/openExternal';
import { Body, Pressable } from '@/components/ui';
import { Icon } from '@/components/icons';
import { BackButton } from '@/components/BackButton';
import { TRIAGE_V2 } from '@/config/features';
import { fetchOrgProfile, type OrgProfile } from '@/services/orgProfile';

const RED = '#C4634C';

/** Ensure a user-entered website opens as an absolute URL. */
const withScheme = (u: string) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

function PhoneIcon({ color }: { color: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.72 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0122 16.92z" />
    </svg>
  );
}

// Phone numbers + websites verified against each charity's official site on
// 2026-06-22. Keep `web` as the public-facing site; `tel` is digits-only.
const HELPLINE_GROUPS = [
  {
    heading: 'Open around the clock',
    lines: [
      { name: 'National Domestic Abuse Helpline', meta: '0808 2000 247 · Women & children · 24/7 · Free, confidential', tel: '08082000247', web: 'nationaldahelpline.org.uk' },
      { name: '24/7 Rape & Sexual Abuse Support Line', meta: '0808 500 2222 · Anyone affected by sexual violence · 24/7', tel: '08085002222', web: '247sexualabusesupport.org.uk' },
    ],
  },
  {
    heading: 'Specialist support for women',
    lines: [
      { name: 'Southall Black Sisters', meta: '0208 571 0800 · Black & minoritised women · Mon to Fri', tel: '02085710800', web: 'southallblacksisters.org.uk' },
      { name: 'Sistah Space', meta: '020 7846 8350 · Women of African & Caribbean heritage · Mon to Fri', tel: '02078468350', web: 'sistahspace.org' },
      { name: "Muslim Women's Network Helpline", meta: '0800 999 5786 · Muslim women · Mon to Fri', tel: '08009995786', web: 'mwnhelpline.co.uk' },
      { name: 'Karma Nirvana', meta: '0800 5999 247 · Honour-based abuse & forced marriage · Mon to Fri', tel: '08005999247', web: 'karmanirvana.org.uk' },
    ],
  },
  {
    heading: 'LGBT+ and men',
    lines: [
      { name: 'Galop', meta: '0800 999 5428 · LGBT+ domestic abuse helpline', tel: '08009995428', web: 'galop.org.uk' },
      { name: "Respect Men's Advice Line", meta: '0808 801 0327 · Male victims · Mon to Fri · Free, confidential', tel: '08088010327', web: 'mensadviceline.org.uk' },
    ],
  },
];

export function CrisisSupport({ onClose }: { onClose: () => void }) {
  // The org that provided this person's access (a charity, employer...) can add a
  // message + their own support contact, shown here. Absent for B2C / ungrouped users.
  const [org, setOrg] = React.useState<OrgProfile | null>(null);
  React.useEffect(() => {
    let live = true;
    fetchOrgProfile().then((o) => { if (live) setOrg(o); }).catch(() => {});
    return () => { live = false; };
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 86,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: 'calc(18px + var(--safe-top)) 22px calc(40px + var(--safe-bottom))' }}>
          {TRIAGE_V2 && <div style={{ marginBottom: 12 }}><BackButton onClick={onClose} /></div>}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 18 }}>
            <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 30, fontWeight: 400, lineHeight: 1.15, color: 'var(--ink)', margin: '2px 0 0' }}>
              You are not alone.
            </h1>
            {!TRIAGE_V2 && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{ width: 44, height: 44, borderRadius: 999, border: '1px solid var(--hair)', background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--edge), var(--sh1)', cursor: 'pointer', flexShrink: 0 }}
            >
              <Icon name="chevDown" size={20} />
            </button>
            )}
          </div>

          {/* 999 — immediate danger */}
          <a
            href="tel:999"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              background: RED,
              color: '#fff',
              borderRadius: 16,
              padding: '16px 18px',
              textDecoration: 'none',
              marginBottom: 14,
              boxShadow: '0 6px 18px rgba(196,99,76,0.28)',
            }}
          >
            <span style={{ display: 'flex' }}>
              <PhoneIcon color="#fff" />
            </span>
            <span>
              <span style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontSize: 19, fontWeight: 500, lineHeight: 1.1 }}>Call 999</span>
              <span style={{ display: 'block', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, opacity: 0.95, marginTop: 2 }}>If you are in immediate danger</span>
            </span>
          </a>

          {org && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 9 }}>
                Your organisation
              </div>
              <div style={{ background: 'var(--card-top)', border: '1px solid var(--hair)', borderRadius: 16, padding: '16px 18px' }}>
                {org.name && (
                  <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 18, fontWeight: 500, lineHeight: 1.2, color: 'var(--ink)', marginBottom: org.member_message ? 8 : 12 }}>
                    {org.name}
                  </div>
                )}
                {org.member_message && (
                  <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, lineHeight: 1.6, color: 'var(--ink)', whiteSpace: 'pre-wrap', marginBottom: 12 }}>
                    {org.member_message}
                  </div>
                )}
                {org.support_phone && (
                  <a
                    href={`tel:${org.support_phone.replace(/\s+/g, '')}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'var(--ink)', padding: '10px 0' }}
                  >
                    <span style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(21,122,89,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <PhoneIcon color="var(--green-deep)" />
                    </span>
                    <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, fontWeight: 600 }}>{org.support_phone}</span>
                  </a>
                )}
                {org.website_url && (
                  <Pressable
                    as="button"
                    onClick={() => openExternal(withScheme(org.website_url as string))}
                    ariaLabel={`Open ${org.name ?? 'the organisation'} website`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green-deep)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, fontWeight: 700, padding: '6px 0' }}
                  >
                    Visit their website
                    <Icon name="chevLeft" size={15} style={{ transform: 'rotate(180deg)' }} />
                  </Pressable>
                )}
              </div>
            </div>
          )}

          {HELPLINE_GROUPS.map((group) => (
            <div key={group.heading} style={{ marginTop: 18 }}>
              <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 9 }}>
                {group.heading}
              </div>
              {group.lines.map((h) => (
                <div
                  key={h.tel}
                  style={{
                    background: 'var(--card-top)',
                    border: '1px solid var(--hair)',
                    borderRadius: 16,
                    padding: '14px 16px',
                    marginBottom: 9,
                  }}
                >
                  <a
                    href={`tel:${h.tel}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, textDecoration: 'none', color: 'var(--ink)' }}
                  >
                    <span style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(196,99,76,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <PhoneIcon color={RED} />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontSize: 16, fontWeight: 500, lineHeight: 1.2, color: 'var(--ink)' }}>{h.name}</span>
                      <span style={{ display: 'block', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 2 }}>{h.meta}</span>
                    </span>
                  </a>
                  {h.web && (
                    <Pressable
                      as="button"
                      onClick={() => openExternal(withScheme(h.web))}
                      ariaLabel={`Open the ${h.name} website`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, marginLeft: 48, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green-deep)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, fontWeight: 700, padding: '2px 0' }}
                    >
                      {h.web}
                      <Icon name="chevLeft" size={13} style={{ transform: 'rotate(180deg)' }} />
                    </Pressable>
                  )}
                </div>
              ))}
            </div>
          ))}

          <Body dim size={13} style={{ textAlign: 'center', marginTop: 18 }}>
            If you are not in danger but need to talk, these lines are here for you, day or night.
          </Body>

          <div style={{ marginTop: 18, display: 'flex', justifyContent: 'center' }}>
            <Pressable
              as="button"
              onClick={onClose}
              ariaLabel="Take me back"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--green-deep)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 13.5,
                fontWeight: 600,
                padding: '8px 4px',
              }}
            >
              Take me back
            </Pressable>
          </div>
        </div>
      </div>
    </div>
  );
}
